import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import createTheme from "@mui/material/styles/createTheme";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import { Link, Outlet, useLocation } from "react-router-dom";
import Stack from "@mui/material/Stack";
import AppHeader from "../shared/header";
import "../shared/styles/theme.scss";
import { List, ListItem, ListItemIcon, ListItemText, PaletteOptions } from "@mui/material";
import DashboardIcon from "../shared/icon/dashboard-icon";
import AccountIcon from "../shared/icon/account-icon";
import AppsIcon from "../shared/icon/apps-icon";
import DevicesIcon from "../shared/icon/devices-icon";
import LocationIcon from "../shared/icon/location-icon";
import CurrencyIcon from "../shared/icon/currency-icon";
import { useArcAuth } from "@arcauth/react-sdk";
import LockIcon from "../shared/icon/lock-icon";
import { useDispatch, useSelector } from "react-redux";
import { getUserInfo } from "../redux/features/user.slice";
import { RootStateType } from "../redux/features/rootSlice";
import { getLoginDeviceInfo } from "../redux/features/auth.slice";
import NetworkToast from "../shared/components/network";


const MainLayout = () => {
  const { isAuthenticated } = useArcAuth();
  let clientConfig :any= useSelector(
    (state: RootStateType) => state.configStateReducer.clientConfig
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const path = location.pathname;
  const leftMenuItem = [
    {
      name: "Dashboard",
      icon: <DashboardIcon />,
      path: "/",
    },
    {
      name: "My account",
      icon: <AccountIcon />,
      path: "/account",
    },
      ...(clientConfig.appType === 'E-commerce' ? [
      {
        name: "Address",
        icon: <LocationIcon />,
        path: "/address",
      }
    ] : []),
    {
      name: "Security",
      icon: <LockIcon />,
      path: "/security",
    },
    // {
    //   name: "Apps",
    //   icon: <AppsIcon />,
    //   path: "/apps",
    // },    
    // {
    //   name: "Subscription",
    //   icon: <CurrencyIcon />,
    //   path: "/subscription",
    // },
  ];
  const defaultTheme = createTheme({
    palette: 
      clientConfig?.palette as PaletteOptions, 
    typography: {
      fontFamily: 'Inter',
      h1: {
        fontSize: '2.5rem',
        fontWeight: '300'
      },
      h2: {
        fontSize: '2rem',
        fontWeight: '700'
      },
      h3: {
        fontSize: '1.7rem',
        fontWeight: '700'
      },
      h4: {
        fontSize: '1.4rem',
        fontWeight: '700'
      },
      h5: {
        fontSize: '1.2rem',
        fontWeight: '700'
      },
      h6: {
        fontSize: '.9rem',
        fontWeight: '700'
      },
      body2: {
        fontSize: '.9rem'
      }
    },
    components: {
  
      MuiButtonBase: {
        defaultProps: {
          sx: {
            textTransform: 'none !important',
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true,
          sx: {
            minWidth: 85,
            textTransform: 'none !important',
  
            '&.MuiButton-containedPrimary': {
              ':hover': {
                background: (theme) => theme.palette.primary.dark
              }
            }
          }
        },
      },
  
      MuiIconButton: {
        defaultProps: {
          sx: {
            minWidth: 40,
            minHeight: 40,
            'svg': {
              width: 20
            }
          }
        }
      },
      MuiMenu: {
        defaultProps: {
          sx: {
            '.MuiPaper-root': {
              minWidth: 200,
              boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
              'li': {
                gap: 1
              }
            },
            'svg': {
              maxWidth: 20,
            }
          }
        }
      },
  
      MuiDialog: {
        defaultProps: {
          sx: {
            '.MuiDialogContent-root': { pt: 1 },
            '.MuiPaper-root': { boxShadow: '4px 9px 30px 0 rgba(0,0,0,.04)' },
            '.MuiDialogActions-root': { px: 3, pb: 3, 'button + button': { ml: 'auto' } },
            'h2': { fontSize: '1rem', fontWeight: 600 },
          }
        }
      },
  
    },
  });
  const storedClientId = useSelector((state:RootStateType)=>state.configStateReducer.clientId);
  useEffect(() => {
    if (isAuthenticated && storedClientId) {   
      setTimeout(() => {
        dispatch(getUserInfo());
        dispatch(getLoginDeviceInfo());
      }, 500);
    }
  }, [storedClientId,isAuthenticated]);
  
  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Container
        maxWidth={false}
        sx={{ px: { xs: 0 }, height: "100%" }}
        data-testid="arcauth-main-layout-component"
      >
        <Stack sx={{display: 'grid', gridTemplateRows: '64px 1fr', height: '100%' }}>
          <NetworkToast/>
          <AppHeader />
          <Stack direction={{md:'row'}} gap={2}>
            <Stack
              component={"nav"}
              width={{xs:'100%', md:'250px'}}
              minHeight={{xs:'auto',md:'calc(100vh - 64px)'}}
              minWidth={250}
              // py={4}
              sx={{
                svg: { minWidth: 20, width: 20, height: 20 },
                backgroundColor: 'white',
              }}
            >
              
              <List 
              component="nav"                   
              aria-label="Main menu"
              sx={{ position: {xs: 'fixed', md: 'sticky'}, top: {xs: 'auto', md: '70px'}, bottom: {xs: '0', md: 'auto'}, display: {xs: 'flex', md: 'block'}, alignItems: {xs: 'center'}, justifyContent: {xs: 'space-around'}, width: {xs: '100%', md: 'auto'}, zIndex:9, background: {xs: '#fff', md: 'transparent'}, '.MuiListItem-root': {
                  justifyContent: {
                    xs: 'center',
                    sm: 'flex-start'
                  }
              } }}>
                {leftMenuItem.map((data, index) => {
                  return (
                    <ListItem
                      sx={{
                        '.MuiListItemText-root': {
                          display: {
                            xs: 'none',
                            sm:'block'
                          },
                        }
                      }}
                      component={Link}
                      to={data.path}
                      button
                      key={index}
                      selected={data.path === path}
                    >
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        {data.icon}{" "}
                      </ListItemIcon>
                      <ListItemText primary={data.name} />
                    </ListItem>
                  );
                })}
              </List>
            </Stack>
            <Box component={"main"} flexGrow={1} py={4} px={2} height={'-webkit-fill-available'}>
              <Outlet />
            </Box>
          </Stack>
        </Stack>
      </Container>
    </ThemeProvider>
  );
};
export default MainLayout;
